import { useMemo, useState } from 'react';
import { CompositeIndexModel } from 'models';
import { MarketDateFilter } from 'models/types';
import {
  Box,
  Flex,
  Text,
  Card,
  CardHeader,
  Badge,
  Button,
  Icon,
  Stack,
} from '@chakra-ui/react';
import RefreshBadge from 'components/RefreshBadge/RefreshBadge';
import IndexChartCore from './IndexChartCore';
import MetricCard from '../Index/MetricCard/MetricCard';
import CompositeIndexChartSkeleton from './Skeletons/CompositeIndexChartSkeleton';
import { RiRefreshLine, RiRestartLine, RiAuctionLine } from 'react-icons/ri';
import { FcClock } from 'react-icons/fc';

interface CompositeIndexChartProps {
  compositeIndexData?: CompositeIndexModel;
  showLastCalculationDate?: boolean;
  useCard?: boolean;
  showTitle?: boolean;
  showFilters?: boolean;
  showMetricCard?: boolean;
  image?: string;
  loading?: boolean;
  onRefreshClick?: () => void;
  onRegenerateClick?: () => void;
  isRefreshing?: boolean;
  isRegenerating?: boolean;
}

export default function CompositeIndexChart({
  compositeIndexData,
  showLastCalculationDate,
  image,
  useCard = false,
  showTitle = true,
  showFilters = true,
  showMetricCard = true,
  loading = false,
  onRefreshClick,
  onRegenerateClick,
  isRefreshing = false,
  isRegenerating = false,
}: CompositeIndexChartProps) {
  const [selectedRange, setSelectedRange] = useState<MarketDateFilter>(MarketDateFilter.MAX);

  const filteredBackfilled = useMemo(() => {
    return compositeIndexData?.filterDataByMarketDateFilter(selectedRange, true);
  }, [compositeIndexData, selectedRange]);

  const metric = filteredBackfilled?.length
    ? filteredBackfilled[filteredBackfilled.length - 1].weightedIndex
    : 0;
  const priorMetric = filteredBackfilled?.length
    ? filteredBackfilled[0].weightedIndex
    : undefined;

  const isUpdating = isRefreshing || isRegenerating;
  const isStale = compositeIndexData?.recalculate;

  const renderStatusIndicator = () => {
    if (isRegenerating) {
      return <RefreshBadge label={'Regenerating'} />;
    }
    if (isRefreshing) {
      return <RefreshBadge label={'Refreshing'} />;
    }
    if (isStale) {
      return (
        <Badge paddingX={2} paddingY={1} borderRadius="md" colorScheme="orange">
          Needs Recalculation
        </Badge>
      );
    }
    return (
      <Badge paddingY={1} borderRadius="md" colorScheme="green">
        Up To Date
      </Badge>
    );
  };

  const renderTitle = () => {
    return (
      <Flex direction={{ base: 'column', md: 'row' }} gap={2} alignItems={{ base: 'flex-start', md: 'center' }}>
        {compositeIndexData?.name && (
          <Text textAlign="left" fontSize="md" fontWeight="bold">
            {compositeIndexData?.name}
          </Text>
        )}
        {renderStatusIndicator()}
      </Flex>
    );
  };

  function renderMetricCard() {
    if (showMetricCard && filteredBackfilled?.length) {
      return (
        <MetricCard
          name={compositeIndexData?.name || 'Composite Edition Index'}
          image={image}
          metric={metric}
          priorMetricValue={priorMetric}
          showBorder={false}
        />
      );
    }
  }

  function renderLastCalculationDate() {
    const lastCalculatedDate = compositeIndexData?.lastCalculationDate?.format('LL');
    const lastCalculatedRelative = compositeIndexData?.lastCalculationDate?.fromNow();
    const sortedIndexData = compositeIndexData?.getDateSortedIndexData() || [];
    const latestIndexDataPoint = sortedIndexData.length ? sortedIndexData[0] : undefined;
    const latestIndexDate = latestIndexDataPoint?.date?.format('LL');

    return (
      <Stack spacing={1} alignItems="flex-start">
        <Flex align="center" gap={2}>
          <Icon as={FcClock} boxSize={4} />
          <Text fontWeight="light" textAlign={'start'} fontSize={'xs'}>
            Last Calculated: {lastCalculatedDate ? `${lastCalculatedDate}${lastCalculatedRelative ? ` (${lastCalculatedRelative})` : ''}` : 'Not available'}
          </Text>
        </Flex>
        {latestIndexDate && (
          <Flex align="center" gap={2}>
            <Icon as={RiAuctionLine} boxSize={4} color="orange.400" />
            <Text fontWeight="light" textAlign={'start'} fontSize={'xs'}>
              Last Sale Date: {latestIndexDate}
            </Text>
          </Flex>
        )}
      </Stack>
    );
  }

  function renderChartContent() {
    return (
      <IndexChartCore
        indexData={compositeIndexData?.backfilledData || []}
        showFilters={showFilters}
        chartId={compositeIndexData?.id}
        onFilterChange={(filter) => setSelectedRange(filter)}
      />
    );
  }

  const renderActionButtons = () => {
    if (!onRefreshClick && !onRegenerateClick) {
      return null;
    }

    return (
      <Flex direction={'row'} gap={2} alignSelf="flex-start">
        {onRefreshClick && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={RiRefreshLine} boxSize={4} />}
            onClick={onRefreshClick}
            isLoading={isRefreshing}
            isDisabled={isUpdating}
          >
            Refresh Index
          </Button>
        )}
        {onRegenerateClick && (
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<Icon as={RiRestartLine} boxSize={4} />}
            onClick={onRegenerateClick}
            isLoading={isRegenerating}
            isDisabled={isUpdating}
          >
            Regenerate Index
          </Button>
        )}
      </Flex>
    );
  };

  if (loading) {
    return (
      <CompositeIndexChartSkeleton
        useCard={useCard}
        showTitle={showTitle}
        showMetricCard={showMetricCard}
        showLastCalculationDate={showLastCalculationDate}
      />
    );
  } else if (useCard) {
    return (
      <Card paddingTop={7} paddingX={4} borderRadius={8} width={'100%'} display={'flex'} flexDirection={'column'}>
        <CardHeader paddingY={0}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            width="100%"
            gap={4}
          >
            <Stack spacing={2} alignItems="flex-start">
              {showTitle && renderTitle()}
              {renderMetricCard()}
            </Stack>
            {renderActionButtons()}
          </Flex>
          {showLastCalculationDate && renderLastCalculationDate()}

        </CardHeader>
        {renderChartContent()}
      </Card>
    );
  } else {
    return (
      <Box>
        <Stack spacing={2} alignItems="flex-start" mb={4}>
          {showTitle && renderTitle()}
          {renderMetricCard()}
          {showLastCalculationDate && renderLastCalculationDate()}
          {renderActionButtons()}
        </Stack>
        {renderChartContent()}
      </Box>
    );
  }
}
